/**
 * Pure helpers behind the results toolbar: filters, sorting, the per-day
 * price strip and the carrier x day calendar. Prices are exact decimal
 * strings and are only compared within one currency (no conversion).
 */
import type { LaneSearch, NormalizedQuote } from '../../../apis/lanePlanner';
import { departureLocalOf } from './localTime';
import {
  carrierOf,
  comparePrice,
  departureOf,
  sortBest,
  transitOf,
  stopsOf,
  vendorNameOf,
} from './quoteView';

export type SortKey = 'best' | 'cheapest' | 'fastest' | 'earliest';

export const SORT_OPTIONS: Array<{ value: SortKey; label: string }> = [
  { value: 'best', label: 'Best' },
  { value: 'cheapest', label: 'Cheapest' },
  { value: 'fastest', label: 'Fastest' },
  { value: 'earliest', label: 'Earliest' },
];

/** Selected values per filter group, e.g. `{ vendor: ['freightos'], stops: ['direct'] }`. */
export type FilterState = Record<string, string[]>;

export interface FilterGroup {
  key: string;
  label: string;
  options: Array<{ value: string; label: string }>;
}

const priceOf = (quote: NormalizedQuote): number =>
  quote.price == null ? Infinity : Number(quote.price);

/** The day a quote departs, in the local time at its origin (the same day its card shows). */
export const departureDay = (quote: NormalizedQuote): string | null =>
  departureLocalOf(quote)?.day ?? null;

/** A tariff or a road quote has no departure and applies to every day (road is priced for the ship date only). */
const isUndatedTariff = (quote: NormalizedQuote): boolean =>
  departureOf(quote) == null &&
  (quote.rate_type === 'tariff' || quote.mode === 'road');

// ---------------------------------------------------------------------------
// Sorting
// ---------------------------------------------------------------------------

const byNumberLastNull = (a: number, b: number): number => {
  if (a === b) return 0;
  return a < b ? -1 : 1;
};

/** `best` is cheapest then fastest; the others sort on their own key, ties by price. Missing values last. */
export const sortQuotes = (
  quotes: NormalizedQuote[],
  sort: SortKey
): NormalizedQuote[] => {
  if (sort === 'best') return sortBest(quotes);
  if (sort === 'cheapest') return [...quotes].sort(comparePrice);
  const key = (quote: NormalizedQuote): number => {
    if (sort === 'fastest') return transitOf(quote);
    const departs = departureOf(quote);
    return departs ? new Date(departs).getTime() : Infinity;
  };
  return [...quotes].sort(
    (a, b) => byNumberLastNull(key(a), key(b)) || comparePrice(a, b)
  );
};

// ---------------------------------------------------------------------------
// Filters
// ---------------------------------------------------------------------------

/** The filters worth offering for this search: vendor (2+), stops, carrier (2+). */
export const filterGroupsFor = (
  search: LaneSearch,
  quotes: NormalizedQuote[]
): FilterGroup[] => {
  const groups: FilterGroup[] = [];
  // Only vendors that returned quotes: any other option would show nothing.
  const vendors = [...new Set(quotes.map((quote) => quote.vendor))];
  if (vendors.length > 1) {
    groups.push({
      key: 'vendor',
      label: 'Vendor',
      options: vendors.map((code) => ({
        value: code,
        label: vendorNameOf(search, code),
      })),
    });
  }
  // trucks have no stops to filter on
  if (!quotes.length || quotes.some((quote) => quote.mode !== 'road')) {
    groups.push({
      key: 'stops',
      label: 'Stops',
      options: [{ value: 'direct', label: 'Non stop only' }],
    });
  }
  const carriers = [...new Set(quotes.map(carrierOf))].sort();
  if (carriers.length > 1) {
    groups.push({
      key: 'carrier',
      label: 'Carrier',
      options: carriers.map((carrier) => ({ value: carrier, label: carrier })),
    });
  }
  return groups;
};

const MATCHERS: Record<
  string,
  (quote: NormalizedQuote, values: string[]) => boolean
> = {
  vendor: (quote, values) => values.includes(quote.vendor),
  carrier: (quote, values) => values.includes(carrierOf(quote)),
  // quotes without an itinerary (tariffs) are never hidden by a stops filter
  stops: (quote) => {
    const stops = stopsOf(quote);
    return stops == null || stops === 0;
  },
};

export const applyFilters = (
  quotes: NormalizedQuote[],
  filters: FilterState
): NormalizedQuote[] => {
  const active = Object.entries(filters).filter(
    ([key, values]) => values.length > 0 && key in MATCHERS
  );
  if (!active.length) return quotes;
  return quotes.filter((quote) =>
    active.every(([key, values]) => MATCHERS[key](quote, values))
  );
};

/** The filters that can still be seen and cleared: only groups and options currently offered. */
export const pruneFilters = (
  filters: FilterState,
  groups: FilterGroup[]
): FilterState =>
  Object.fromEntries(
    groups.map((group) => [
      group.key,
      (filters[group.key] ?? []).filter((value) =>
        group.options.some((option) => option.value === value)
      ),
    ])
  );

export const activeFilterCount = (filters: FilterState): number =>
  Object.values(filters).reduce((sum, values) => sum + values.length, 0);

// ---------------------------------------------------------------------------
// Day strip and calendar
// ---------------------------------------------------------------------------

export interface DayCell {
  day: string;
  /** Lowest price that day, or null when nothing departs or applies that day. */
  price: string | null;
}

export interface DayStrip {
  currency: string | null;
  days: DayCell[];
}

/** The currency most quotes are priced in (USD wins a tie), for the single-price strip. */
const stripCurrency = (quotes: NormalizedQuote[]): string | null => {
  const counts = new Map<string, number>();
  for (const quote of quotes) {
    if (quote.price != null && quote.currency) {
      counts.set(quote.currency, (counts.get(quote.currency) ?? 0) + 1);
    }
  }
  let best: string | null = null;
  for (const [currency, count] of counts) {
    const bestCount = best ? (counts.get(best) ?? 0) : -1;
    if (count > bestCount || (count === bestCount && currency === 'USD')) {
      best = currency;
    }
  }
  return best;
};

/** Every day in the search window (the server's day prices) plus any day a quote departs. */
export const windowDays = (search: LaneSearch): string[] => {
  const days = new Set<string>();
  for (const quote of search.quotes) {
    const day = departureDay(quote);
    if (day) days.add(day);
  }
  for (const currencies of Object.values(search.day_prices)) {
    for (const prices of Object.values(currencies)) {
      Object.keys(prices).forEach((day) => days.add(day));
    }
  }
  return [...days].sort();
};

const cheaper = (a: NormalizedQuote | null, b: NormalizedQuote): boolean =>
  a == null || priceOf(b) < priceOf(a);

/** Lowest-priced quote of `quotes` on `day`: dated that day, or an undated tariff (applies to every day). */
const cheapestOn = (
  quotes: NormalizedQuote[],
  day: string,
  currency: string
): NormalizedQuote | null => {
  let best: NormalizedQuote | null = null;
  for (const quote of quotes) {
    if (quote.price == null || quote.currency !== currency) continue;
    if (departureDay(quote) !== day && !isUndatedTariff(quote)) continue;
    if (cheaper(best, quote)) best = quote;
  }
  return best;
};

/** Whether any priced quote departs on a day of its own: without one every day shows the same tariff price, so a per-day strip says nothing. */
export const hasDatedPrices = (quotes: NormalizedQuote[]): boolean =>
  quotes.some((quote) => quote.price != null && departureDay(quote) != null);

/** The strip's days with their lowest price among the (filtered) quotes. */
export const buildDayStrip = (
  search: LaneSearch,
  quotes: NormalizedQuote[]
): DayStrip => {
  const currency = stripCurrency(quotes);
  const days = windowDays(search).map((day) => ({
    day,
    price:
      currency == null
        ? null
        : (cheapestOn(quotes, day, currency)?.price ?? null),
  }));
  return { currency, days };
};

/** Keeps quotes departing on `day`, plus undated tariffs (they apply to any day). */
export const filterByDay = (
  quotes: NormalizedQuote[],
  day: string | null
): NormalizedQuote[] =>
  day == null
    ? quotes
    : quotes.filter(
        (quote) => departureDay(quote) === day || isUndatedTariff(quote)
      );

export interface CalendarRow {
  carrier: string;
  cells: Record<string, { price: string; quote: NormalizedQuote } | undefined>;
}

/** Carrier x day grid of each carrier's lowest price, cheapest carrier first. */
export const buildCalendar = (
  search: LaneSearch,
  quotes: NormalizedQuote[]
): { currency: string | null; days: string[]; rows: CalendarRow[] } => {
  const currency = stripCurrency(quotes);
  const days = windowDays(search);
  if (currency == null) return { currency, days, rows: [] };
  const byCarrier = new Map<string, NormalizedQuote[]>();
  for (const quote of quotes) {
    const carrier = carrierOf(quote);
    const bucket = byCarrier.get(carrier);
    if (bucket) bucket.push(quote);
    else byCarrier.set(carrier, [quote]);
  }
  const rows: CalendarRow[] = [];
  for (const [carrier, carrierQuotes] of byCarrier) {
    const cells: CalendarRow['cells'] = {};
    for (const day of days) {
      const quote = cheapestOn(carrierQuotes, day, currency);
      if (quote?.price != null) cells[day] = { price: quote.price, quote };
    }
    if (Object.keys(cells).length) rows.push({ carrier, cells });
  }
  const floor = (row: CalendarRow): number =>
    Math.min(...Object.values(row.cells).map((c) => Number(c?.price)));
  return { currency, days, rows: rows.sort((a, b) => floor(a) - floor(b)) };
};
